import { useStore } from '@tanstack/react-form'
import { Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { z } from 'zod'

import type { OrderWithStock, PostOrdersPrepareBody } from '@snootsvet/shared'
import { pluralizeUnit } from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { FormError } from '@/frontend/components/form/primitives'
import { zodErrorToFieldErrors } from '@/frontend/components/form/validation'
import { Alert, AlertDescription } from '@/frontend/components/ui/alert'
import { Button } from '@/frontend/components/ui/button'
import { useListPatientOrders } from '@/frontend/features/orders/hooks/use-list-patient-orders'
import {
  AllocationProgressBadge,
  type EnrichedAllocationItem,
  StockAllocationResetButton,
  validateStockAllocations,
} from '@/frontend/features/orders/stock-allocation.utils'
import { StockAllocationEditor } from '@/frontend/features/orders/stock-allocation-editor'
import { computeStockAllocation } from '@/frontend/lib/stock'

const orderSchema = z
  .object({
    id: z.string(),
    quantity: z.number().positive(),
    allocations: z.array(
      z.object({
        batchNumber: z.string().nullable(),
        quantity: z.number().min(0),
      })
    ),
  })
  .superRefine((v, ctx) => validateStockAllocations(v.allocations, v.quantity, ctx))
  .transform((v) => ({
    id: v.id,
    allocations: v.allocations.filter((a) => a.quantity > 0),
  }))

const schema = z.object({ orders: z.array(orderSchema).min(1) })

interface OrderAllocationFormProps {
  patientId: string
  orderIds: string[]
  onSubmit: (body: PostOrdersPrepareBody) => Promise<void>
  onCancel: () => void
}

export function OrderAllocationForm({
  patientId,
  orderIds,
  onSubmit,
  onCancel,
}: OrderAllocationFormProps) {
  const { data, isError } = useListPatientOrders(patientId, {
    include: 'stock',
    refetchInterval: 10_000,
  })
  const orders = orderIds
    .map((id) => data?.find((order) => order.id === id))
    .filter((order) => order !== undefined)

  if (!data || orders.length !== orderIds.length) {
    return (
      <div className="flex flex-col gap-4">
        {isError || data ? (
          <Alert variant="destructive">
            <AlertDescription>
              {isError ? 'Could not refresh orders.' : "This patient's orders have changed."} Please
              close this form and review the current checkout state.
            </AlertDescription>
          </Alert>
        ) : (
          <p className="text-muted-foreground">Loading orders…</p>
        )}
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    )
  }

  return (
    <OrderAllocationFormInner
      orders={orders}
      refreshFailed={isError}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  )
}

function OrderAllocationFormInner({
  orders,
  refreshFailed,
  onSubmit,
  onCancel,
}: {
  orders: OrderWithStock[]
  refreshFailed: boolean
  onSubmit: OrderAllocationFormProps['onSubmit']
  onCancel: () => void
}) {
  const stockData = useMemo(
    () =>
      orders.map((order) =>
        computeStockAllocation(
          order.product.stock.filter((s) => s.clinicId === order.clinicId),
          order.quantity
        )
      ),
    [orders]
  )

  const [defaultValues] = useState(() => ({
    orders: orders.map((order, index) => ({
      id: order.id,
      quantity: order.quantity,
      allocations: stockData[index].allocations as EnrichedAllocationItem[],
    })),
  }))
  const orderChanged = orders.some(
    (order, index) =>
      order.status !== 'pending' || order.quantity !== defaultValues.orders[index].quantity
  )
  const blockedMessage = refreshFailed
    ? 'Could not refresh orders. Please close this form and review the current checkout state.'
    : orderChanged
      ? "This patient's orders have changed. Please close this form and review the current checkout state."
      : null

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: ({ value }) => {
        if (blockedMessage) return { form: blockedMessage, fields: {} }
        const res = schema.safeParse(value)
        if (!res.success) return zodErrorToFieldErrors(res.error)
      },
    },
    onSubmit: async ({ value, formApi }) => {
      try {
        await onSubmit(schema.parse(value))
      } catch (error) {
        formApi.setErrorMap({
          onSubmit: {
            form: error instanceof Error ? error.message : 'Could not prepare items',
            fields: {},
          },
        })
      }
    },
  })

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting)

  return (
    <form.AppForm>
      <div className="flex flex-col gap-4">
        {blockedMessage && (
          <Alert variant="destructive">
            <AlertDescription>{blockedMessage}</AlertDescription>
          </Alert>
        )}
        <fieldset
          disabled={isSubmitting || !!blockedMessage}
          className="flex min-w-0 flex-col gap-4"
        >
          {orders.map((order, index) => (
            <div key={order.id} className="flex flex-col gap-2 rounded-md border p-3">
              <p className="text-sm font-medium">
                {order.product.name} × {order.quantity}{' '}
                {pluralizeUnit(order.product.unit, order.quantity)}
              </p>
              <form.AppField name={`orders[${index}].allocations`}>
                {(field) => (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <AllocationProgressBadge
                        allocated={field.state.value.reduce((sum, a) => sum + a.quantity, 0)}
                        required={order.quantity}
                      />
                      <StockAllocationResetButton
                        onReset={() =>
                          field.setValue(stockData[index].allocations as EnrichedAllocationItem[])
                        }
                      />
                    </div>
                    <FormError errors={field.state.meta.errors} />
                    <StockAllocationEditor
                      allocations={field.state.value}
                      onChange={field.setValue}
                      quantity={order.quantity}
                      availableBatches={stockData[index].availableBatches}
                      stockStatus={stockData[index].stockStatus}
                      clinicId={order.clinicId}
                      productName={order.product.name}
                    />
                  </div>
                )}
              </form.AppField>
            </div>
          ))}
        </fieldset>
        <form.GlobalError />
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="lg"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="lg"
            disabled={isSubmitting || !!blockedMessage}
            onClick={() => form.handleSubmit()}
          >
            {isSubmitting && <Loader2 className="size-3.5 mr-1.5 animate-spin" />}
            Confirm
          </Button>
        </div>
      </div>
    </form.AppForm>
  )
}
